import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Reviews from "./Reviews";
import { useCompare } from "../CompareContext";
import {
    FLAG_ICONS_COLORS_FOOD,
    FLAG_LABELS,
    getProductImage,
    VITAMINS_MINERALS_ICONS
} from "../food-constants";
import "./VitaminPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTimes,
    faHeartbeat,
    faExclamationTriangle,
    faSyncAlt,
    faThumbsUp,
    faThumbsDown,
    faCubes
} from "@fortawesome/free-solid-svg-icons";
import { getScoreStyle } from "../constants";

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

const IngredientModal = ({ ingredient, onClose, position }) => {
    if (!ingredient) return null

    const {
        main_name,
        key_health_benefits = [],
        deficiency = {},
        side_effects_and_overdose = [],
        interactions = {},
    } = ingredient

    return (
        <div className="modal-overlay-custom" onClick={onClose}>
            <div
                className="modal-content-custom"
                onClick={(e) => e.stopPropagation()}
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                }}
            >
                <div className="modal-header">
                    <h2 className="modal-ingredient-name">{main_name || ingredient.raw_name}</h2>
                    <button className="modal-close-button" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body">
                    {key_health_benefits.length > 0 && (
                        <div className="modal-section">
                            <h3 className="modal-section-title">
                                <FontAwesomeIcon icon={faHeartbeat} /> Faydaları
                            </h3>
                            <ul className="modal-list">
                                {key_health_benefits.map((benefit, i) => (
                                    <li key={i}>{benefit}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {deficiency.signs?.length > 0 && (
                        <div className="modal-section">
                            <h3 className="modal-section-title">
                                <FontAwesomeIcon icon={faExclamationTriangle} /> Eksiklik Belirtileri
                            </h3>
                            <ul className="modal-list">
                                {deficiency.signs.map((sign, i) => (
                                    <li key={i}>{sign}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {side_effects_and_overdose.length > 0 && (
                        <div className="modal-section">
                            <h3 className="modal-section-title">
                                <FontAwesomeIcon icon={faExclamationTriangle} /> Yan Etkiler / Aşırı Doz
                            </h3>
                            <ul className="modal-list">
                                {side_effects_and_overdose.map((effect, i) => (
                                    <li key={i}>{effect}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(interactions.synergies?.length > 0 || interactions.negative_interactions?.length > 0) && (
                        <div className="modal-section">
                            <h3 className="modal-section-title">
                                <FontAwesomeIcon icon={faSyncAlt} /> Etkileşimler
                            </h3>
                            {interactions.synergies?.length > 0 && (
                                <div className="modal-subsection">
                                    <h4 className="modal-subsection-title">
                                        <FontAwesomeIcon icon={faThumbsUp} /> Olumlu Etkileşimler
                                    </h4>
                                    <ul className="modal-list">
                                        {interactions.synergies.map((syn, i) => (
                                            <li key={i}>{syn}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {interactions.negative_interactions?.length > 0 && (
                                <div className="modal-subsection">
                                    <h4 className="modal-subsection-title">
                                        <FontAwesomeIcon icon={faThumbsDown} /> Olumsuz Etkileşimler
                                    </h4>
                                    <ul className="modal-list">
                                        {interactions.negative_interactions.map((neg, i) => (
                                            <li key={i}>{neg}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const VitaminPage = ({ product }) => {
    const [activeTab, setActiveTab] = useState("genel");
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const [expandedFlag, setExpandedFlag] = useState(null);
    const [expandedIngred, setExpandedIngred] = useState(null);

    const navigate = useNavigate();
    const { addToCompare } = useCompare();

    const handleCompareClick = () => {
        addToCompare(product);
        navigate("/select-products");
    };

    const handleIngredientClick = (ingredientObj, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const modalWidth = 300;
        const modalHeight = 320;
        const padding = 10;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const viewWidth = window.innerWidth;
        const viewHeight = window.innerHeight;

        let left = rect.left + scrollX - (modalWidth - rect.width) / 2;
        let top = rect.top + scrollY + rect.height + padding;

        // Adjust if going out of screen
        if (top + modalHeight > scrollY + viewHeight - padding) {
            top = rect.top + scrollY - modalHeight - padding;
        }
        left = Math.max(scrollX + padding, Math.min(left, scrollX + viewWidth - modalWidth - padding));
        top = Math.max(scrollY + padding, top);

        setModalPosition({ top, left });
        setSelectedIngredient(ingredientObj);
    };

    const renderFlagElements = (flags) => {
        if (!product?.composition_analysis?.flags) return null;
        const flagsObj = product.composition_analysis.flags;

        return flags.map((flagKey) => {
            const isPositive = !!flagsObj[flagKey];
            if (typeof isPositive !== "boolean") return null;
            const isExpanded = expandedFlag === flagKey;

            return (
                <div
                    key={flagKey}
                    className={`flag-item ${isPositive ? "free" : "not-free"} ${isExpanded ? "expanded" : ""}`}
                    onClick={!isPositive ? () => setExpandedFlag(isExpanded ? null : flagKey) : undefined}                >
                    <div className="flag-header">
                        <div
                            className="flag-icon"
                            style={{ color: FLAG_ICONS_COLORS_FOOD[flagKey]?.color }}
                        >
                            {FLAG_ICONS_COLORS_FOOD[flagKey]?.icon}
                        </div>
                        <p>
                            <strong>{FLAG_LABELS[flagKey]} </strong>
                            {flagKey === "vegan" ? (
                                <strong>{isPositive ? "" : "Değil"}</strong>
                            ) : (
                                <strong>{isPositive ? "İçermez" : "İçerir"}</strong>
                            )}
                        </p>
                    </div>
                </div>
            );
        });
    };

    const renderExpandedContent = (flags) => {
        if (product?.composition_analysis?.flags) {
            return (
                <>
                    {flags.map((flag, index) => {
                        const isExpanded = expandedFlag === flag
                        if (!isExpanded) return null

                        const flagsObj = product.composition_analysis.flags;
                        const reason = flagsObj[`${flag}_reason`] || [];
                        return (
                            <div key={index} className="flag-details">
                                {reason.length > 0 && (
                                    <div className="violating-ingredients">
                                        <h4>⚠️ İlgili İçerikler:</h4>
                                        <div className="ingredient-list">
                                            {reason.map((ingredient, i) => {
                                                return (
                                                    <div
                                                        className={`ingredient-row`}
                                                        key={i}
                                                    >
                                                        <div className="modal-ingredient-name-wrapper">
                                                            <span className="ingredient-name">
                                                                {capitalizeFirstLetter(ingredient)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </>
            )
        }
    }

    const renderRisks = () => {
        const activeItems = product.active_ingredients_analysis || [];
        const allSideEffects = activeItems.flatMap((ing) => ing.side_effects_and_overdose || []);
        if (allSideEffects.length === 0) {
            return <p>Ciddi risk bilgisi bulunamadı.</p>;
        }
        return (
            <div className="section">
                <h2>Riskler / Yan Etkiler</h2>
                <div className="risk-cards">
                    {allSideEffects.map((effect, idx) => (
                        <div key={idx} className="risk-card">
                            <span className="risk-icon">⚠️</span>
                            <span className="risk-text">{effect}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderCompositionSection = () => {
        return (
            <div className="section">
                <div className="ingredient-forms-section">
                    <h2>İçerik Formları</h2>
                    <div className="ingredient-forms-grid">
                        {product.active_ingredients_analysis.map((ingredient, idx) => (
                            <div key={idx} className="ingredient-form-card">
                                <div className="ingredient-form-header">
                                    <FontAwesomeIcon icon={faCubes} style={{ marginRight: "8px", color: "#1976d2" }} />
                                    <h4>{ingredient.main_name}</h4>
                                    <p>{ingredient.amount}</p>
                                </div>
                                <div className="ingredient-form-details">
                                    {ingredient.forms.map((form, i) => (
                                        <div key={i} className="form-card">
                                            <strong>{capitalizeFirstLetter(form.form_name)}</strong>
                                            <p>{form.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <h2>Diğer İçerikler</h2>
                <div className="composition-section">
                    <div className="composition-summary">
                        <p>{product.composition}</p>
                    </div>
                </div>
            </div>
        );
    };

    const allFlags = Object.keys(FLAG_LABELS);

    return (
        <div className="hair-page">
            <div className="product-card">
                <div className="image-container">
                    <img
                        src={getProductImage("food", product.barcode)}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = product.image;
                        }}
                        alt={product.name}
                        className="product-image"
                    />
                    <div className="score-circle" style={getScoreStyle(product.total_score)}>
                        <span className="score-big">{product.total_score}</span>
                        <span className="score-small">/100</span>
                    </div>
                </div>
                <div className="text-content">
                    <h1 className="product-name">{product.name}</h1>
                    <h2 className="brand">{product.brand}</h2>

                    {product.daily_take && (
                        <div style={{ margin: "8px 0" }}>
                            <strong>Kullanım:</strong> {product.daily_take}
                        </div>
                    )}

                    <button onClick={handleCompareClick} className="start-compare-button">
                        Karşılaştır
                    </button>
                </div>
            </div>

            <div className="tabs-container">
                <button
                    className={`tab-button ${activeTab === "genel" ? "active" : ""}`}
                    onClick={() => setActiveTab("genel")}
                >
                    Genel
                </button>
                <button
                    className={`tab-button ${activeTab === "bilesim" ? "active" : ""}`}
                    onClick={() => setActiveTab("bilesim")}
                >
                    Tüm İçerikler
                </button>
                <button
                    className={`tab-button ${activeTab === "riskler" ? "active" : ""}`}
                    onClick={() => setActiveTab("riskler")}
                >
                    Riskler
                </button>
                <button
                    className={`tab-button ${activeTab === "yorumlar" ? "active" : ""}`}
                    onClick={() => setActiveTab("yorumlar")}
                >
                    Yorumlar
                </button>
            </div>

            <div className="tab-content">
                {activeTab === "genel" && (
                    <>
                        <div className="section flags">
                            <div className="flags-grid">{renderFlagElements(allFlags.slice(0, 2))}</div>
                            {renderExpandedContent(allFlags.slice(0, 2))}
                            <div className="flags-grid" style={{ marginTop: "12px" }}>
                                {renderFlagElements(allFlags.slice(2, 4))}
                            </div>
                            {renderExpandedContent(allFlags.slice(2, 4))}
                        </div>
                        {product.active_ingredients_analysis &&
                            (
                                <div className="section">
                                    <h2>Aktif İçerikler</h2>
                                    <div className="effect-list">
                                        {product.active_ingredients_analysis.map((ingredient, idx) => {
                                            const isExpanded = expandedIngred === ingredient;
                                            return (
                                                <div
                                                    key={idx}
                                                    className={`notable-effect-container ${isExpanded ? "expanded" : ""}`}
                                                >
                                                    <div
                                                        className="notable-effect-header"
                                                        onClick={() => setExpandedIngred(isExpanded ? null : ingredient)}
                                                    >
                                                        <div className="effect-icon">{VITAMINS_MINERALS_ICONS[ingredient.main_name.toLowerCase()]?.icon}</div>
                                                        <span className="effect-title">{ingredient.main_name}</span>
                                                        <div className="effect-expand-indicator">{isExpanded ? "▲" : "▼"}</div>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="food-ingredient-details">
                                                            {ingredient.key_health_benefits &&
                                                                ingredient.key_health_benefits.length > 0 && (
                                                                    <div>
                                                                        <em>{ingredient.key_health_benefits.join(" / ")}</em>
                                                                    </div>
                                                                )}

                                                            {product.daily_take && ingredient.amount && (
                                                                <div className="ingredient-amount">
                                                                    <i className="fas fa-capsules"></i> {product.daily_take} {ingredient.amount}
                                                                </div>
                                                            )}

                                                            {ingredient.daily_need_coverage && (
                                                                <div className="coverage-container">
                                                                    <div className="coverage-bar-container">
                                                                        <div
                                                                            className="coverage-fill"
                                                                            style={{
                                                                                width: `${Math.min(ingredient.daily_need_coverage, 100)}%`,
                                                                            }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className="coverage-text">
                                                                        <i className="fas fa-percent"></i> Günlük ihtiyacın %{Math.round(ingredient.daily_need_coverage)}’ini karşılar.
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div
                                                                className="more-info"
                                                                style={{
                                                                    marginTop: "10px",
                                                                    fontSize: "0.9rem",
                                                                    fontStyle: "italic",
                                                                    cursor: "pointer",
                                                                    color: "#1976d2",
                                                                    textDecoration: "underline",
                                                                    alignSelf: "flex-end",
                                                                    marginLeft: "4px"
                                                                }}
                                                                onClick={(e) => handleIngredientClick(ingredient, e)}
                                                            >
                                                                Daha Fazla Öğren
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        }
                    </>
                )}

                {activeTab === "bilesim" && renderCompositionSection()}

                {activeTab === "riskler" && (
                    <div className="section">
                        {renderRisks()}
                    </div>
                )}

                {activeTab === "yorumlar" && (
                    <div className="section reviews">
                        <Reviews
                            starRating={product.star_rating?.toString()}
                            barcode={product.barcode}
                            category={product.category}
                        />
                    </div>
                )}
            </div>

            {selectedIngredient && (
                <IngredientModal
                    ingredient={selectedIngredient}
                    onClose={() => setSelectedIngredient(null)}
                    position={modalPosition}
                />
            )}
        </div>
    );
};

export default VitaminPage;
